import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser, selectIsLoading, selectIsInitialized } from '../../store/authSlice';
import LoadingSpinner from '../common/LoadingSpinner';

const PrivateRoute = ({ 
  children, 
  requiredRole, 
  requiredRoles, 
  fallbackPath = '/login',
  unauthorizedPath = '/unauthorized',
  showLoading = true 
}) => {
  const user = useSelector(selectUser);
  const isLoading = useSelector(selectIsLoading);
  const isInitialized = useSelector(selectIsInitialized);
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (!isInitialized || (isLoading && showLoading)) {
    return <LoadingSpinner />;
  }

  // User is not authenticated
  if (!user) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check for single required role
  if (requiredRole) {
    if (!user.roles || !user.roles.includes(requiredRole.toUpperCase())) {
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  // Check for multiple required roles (user must have ALL roles)
  if (requiredRoles && requiredRoles.length > 0) {
    if (!user.roles || !requiredRoles.every(role => user.roles.includes(role.toUpperCase()))) {
      return <Navigate to={unauthorizedPath} replace />;
    }
  }

  // User is authenticated and has required roles
  return children;
};

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredRole: PropTypes.string,
  requiredRoles: PropTypes.arrayOf(PropTypes.string),
  fallbackPath: PropTypes.string,
  unauthorizedPath: PropTypes.string,
  showLoading: PropTypes.bool
};

export default PrivateRoute;
